/**
 * Paperbloom live mockups: renders the 5 product scenes (gift with bow, sheet + roll,
 * hands unrolling, ribbon gift, unrolled roll) from the shopper's live design canvas.
 *
 *   PBLive.mount(canvasEls, sourceCanvas, { repeatIn: 20 })  -> true | false (no WebGL2)
 *   PBLive.update()              // after the source canvas changes; coalesced to one frame
 *   PBLive.add(canvas, "s4")     // extra target, e.g. the main stage while a mockup slide shows
 *   PBLive.remove(canvas)
 *
 * Targets are <canvas> elements. Their scene comes from data-pb-scene, otherwise from
 * their position in upload order: s1, s2, s4, s3, s5. A target gets class
 * "pb-live-on" and fires a "pblive:ready" event on its first live frame. Until then,
 * and always without WebGL2, the caller's static mockup image stays in place.
 *
 * How it works: each scene was baked once through its Photoshop PSD (pb-mockup-flow/live).
 *   base  K   the scene with the design flat black
 *   light a   } per-pixel response to design colour c, fitted from white / 50% grey /
 *   curve b   } black renders: out = K + c*(a + b*c)
 *   uva/uvb   which point of the design each pixel shows (16-bit split into bytes,
 *             half size), so warps, curls and folds come from the PSD itself. Where the
 *             scene shows the paper's underside (the curl), it renders plain white paper.
 * Scene textures (~1.3MB total, opaque .bin so the CDN can't transcode them) load only
 * after the page has painted and gone idle. The UV data is checksummed on the GPU before
 * anything renders; a mismatch disables PBLive and the static images stay.
 */
(() => {
  "use strict";

  // Baked by pb-mockup-flow/live/pack.py (19in roll, 20in repeat). Per smart object:
  // [psb width, psb height, psb px per inch, back]. back = 1 marks where the scene shows
  // the paper's UNDERSIDE (the curl in s4/s5), which prints plain white.
  const SCENES = {
    s1: { n: 3, t: [[1303, 940, 108], [500, 325, 108], [500, 325, 108]] },
    s2: { n: 2, t: [[1043, 1508, 54.9], [1043, 115, 54.9]] },
    s3: { n: 1, t: [[2796, 2672, 233]] },
    // r turns the design a quarter turn (1 = clockwise, -1 = counter-clockwise). The top of the print always
    // faces the roll: upright on the hanging roll (s5, roll across the top), so in the hands scene, where the
    // roll stands on the left and the paper pulls out to the right, the design lies on its side, top to the left.
    s4: { n: 3, r: -1, t: [[285, 2355, 123.9], [608, 2566, 123.9, 1], [1473, 2355, 123.9]] },
    s5: { n: 3, t: [[1300, 150, 68.4], [1300, 443, 68.4, 1], [1300, 1477, 68.4]] },
  };
  const ORDER = ["s1", "s2", "s4", "s3", "s5"];
  // Kill switch: sum of the R+G+B bytes (mod 2^32) of each UV texture as baked. Anything
  // that re-encodes them in transit (e.g. the CDN serving lossy WebP for a .png) garbles
  // the mockups, so a mismatch disables PBLive and the static images stay.
  const UV_SUM = {
    s1: [30737930, 76146816], s2: [40348497, 83279432], s3: [35851080, 86242007],
    s4: [91673083, 136147023], s5: [42455955, 98696582],
  };
  const FILES = ["base", "light", "curve", "uva", "uvb"];
  const MAX_PX = 1000; // baked resolution; larger targets would only upscale
  const MAX_PATTERN = 2048;

  // Theme assets share one CDN folder; derive it from this script's own URL.
  const script = document.currentScript;
  const ASSET_DIR = script && script.src ? script.src.replace(/[^/]*$/, "") : "";
  const assetUrl = (sid, name) => `${ASSET_DIR}pb-live-${sid}-${name}.bin`;

  const VS = `#version 300 es
in vec2 p; out vec2 vUv;
void main(){ vUv = vec2(p.x*.5+.5, .5-p.y*.5); gl_Position = vec4(p,0,1); }`;

  const FS = `#version 300 es
precision highp float;
in vec2 vUv; out vec4 o;
uniform sampler2D uBase, uLight, uCurve, uUvA, uUvB, uPat;
uniform vec4 uT[4];
uniform float uN, uRot;
uniform vec2 uRepeat;
vec4 uvAt(ivec2 p){
  p = clamp(p, ivec2(0), textureSize(uUvA, 0) - 1);
  vec3 A = texelFetch(uUvA, p, 0).rgb * 255., B = texelFetch(uUvB, p, 0).rgb * 255.;
  return vec4(vec2(A.r*256. + A.g, A.b*256. + B.r) / 65535., B.g, B.b);
}
vec4 targetOf(vec4 s){ return uT[int(clamp(floor(s.z/255.*uN), 0., uN-1.))]; }
vec2 patAt(vec4 s){
  vec4 t = targetOf(s);
  vec2 q = (s.xy*t.xy - t.xy*.5) / t.z;
  if (uRot > .5) q = vec2(q.y, -q.x);
  else if (uRot < -.5) q = vec2(-q.y, q.x);
  return q / uRepeat + .5;
}
// Design colour one UV sample shows: the pattern, or plain white paper on the underside.
vec3 colourAt(vec4 s, vec2 gx, vec2 gy){
  return targetOf(s).w > .5 ? vec3(1.) : textureGrad(uPat, patAt(s), gx, gy).rgb;
}
void main(){
  // UV is stored as split bytes at half size, so filter by hand. Inside one continuous
  // smart object: interpolate UV. Across smart-object edges and the hands scene's fold:
  // blend the pattern colours of the 4 texels instead (a soft edge, like Photoshop's
  // anti-aliasing; picking one texel speckles).
  vec2 f = vUv * vec2(textureSize(uUvA, 0)) - .5;
  ivec2 i = ivec2(floor(f)); vec2 w = fract(f);
  vec4 s00 = uvAt(i), s10 = uvAt(i + ivec2(1,0)), s01 = uvAt(i + ivec2(0,1)), s11 = uvAt(i + ivec2(1,1));
  vec4 lo = min(min(s00, s10), min(s01, s11)), hi = max(max(s00, s10), max(s01, s11));
  bool smooth_ = hi.z == lo.z && max(hi.x - lo.x, hi.y - lo.y) < .02;
  vec4 sm = mix(mix(s00, s10, w.x), mix(s01, s11, w.x), w.y);
  vec2 pc = patAt(sm);
  // Mip level from the interpolated mapping; capped so an edge doesn't pick a blurry level.
  vec2 gx = dFdx(pc), gy = dFdy(pc);
  float big = max(length(gx), length(gy));
  if (big > .02) { gx *= .002/big; gy *= .002/big; }
  vec3 c;
  if (smooth_) c = targetOf(s00).w > .5 ? vec3(1.) : textureGrad(uPat, pc, gx, gy).rgb;
  else {
    vec4 k = vec4((1.-w.x)*(1.-w.y), w.x*(1.-w.y), (1.-w.x)*w.y, w.x*w.y)
           * step(1., vec4(s00.w, s10.w, s01.w, s11.w));
    c = (colourAt(s00, gx, gy)*k.x + colourAt(s10, gx, gy)*k.y + colourAt(s01, gx, gy)*k.z + colourAt(s11, gx, gy)*k.w)
       / max(k.x + k.y + k.z + k.w, 1e-4);
  }
  c *= step(1., hi.w);
  vec3 K = texture(uBase, vUv).rgb, a = texture(uLight, vUv).rgb, b = texture(uCurve, vUv).rgb*2. - 1.;
  o = vec4(K + c*(a + b*c), 1.);
}`;

  let gl = null, glCanvas = null, prog = null, uni = null, patTex = null, patAspect = 1;
  let source = null, repeatIn = 20, dead = false, loading = false, raf = 0, patternDirty = true;
  const scenes = {};                 // sid -> [5 textures] once loaded
  const targets = new Map();         // canvas -> { sid, ready }
  const visible = new WeakMap();     // canvas -> bool (IntersectionObserver)
  let io = null;

  function supported() {
    if (dead) return false;
    if (gl) return true;
    try {
      glCanvas = document.createElement("canvas");
      gl = glCanvas.getContext("webgl2", { antialias: false, alpha: false, preserveDrawingBuffer: false, powerPreference: "low-power" });
    } catch (_) { gl = null; }
    if (!gl) { dead = true; return false; }
    glCanvas.addEventListener("webglcontextlost", (e) => { e.preventDefault(); dead = true; });
    try { prog = program(); } catch (err) { console.warn("[PBLive]", err); dead = true; gl = null; return false; }
    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(prog, "p");
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
    gl.useProgram(prog);
    uni = {};
    for (const n of ["uBase", "uLight", "uCurve", "uUvA", "uUvB", "uPat", "uT", "uN", "uRot", "uRepeat"]) uni[n] = gl.getUniformLocation(prog, n);
    ["uBase", "uLight", "uCurve", "uUvA", "uUvB", "uPat"].forEach((n, k) => gl.uniform1i(uni[n], k));
    return true;
  }

  function program() {
    const sh = (type, src) => {
      const s = gl.createShader(type);
      gl.shaderSource(s, src);
      gl.compileShader(s);
      if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s));
      return s;
    };
    const p = gl.createProgram();
    gl.attachShader(p, sh(gl.VERTEX_SHADER, VS));
    gl.attachShader(p, sh(gl.FRAGMENT_SHADER, FS));
    gl.linkProgram(p);
    if (!gl.getProgramParameter(p, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(p));
    return p;
  }

  function texture(img, { nearest = false, repeat = false, mip = false } = {}, existing = null) {
    const t = existing || gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, t);
    gl.pixelStorei(gl.UNPACK_COLORSPACE_CONVERSION_WEBGL, gl.NONE); // UV bytes must arrive untouched
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, gl.RGBA, gl.UNSIGNED_BYTE, img);
    if (!existing) {
      const wrap = repeat ? gl.REPEAT : gl.CLAMP_TO_EDGE;
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, wrap);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, wrap);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, nearest ? gl.NEAREST : mip ? gl.LINEAR_MIPMAP_LINEAR : gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, nearest ? gl.NEAREST : gl.LINEAR);
      const aniso = mip && gl.getExtension("EXT_texture_filter_anisotropic");
      if (aniso) gl.texParameterf(gl.TEXTURE_2D, aniso.TEXTURE_MAX_ANISOTROPY_EXT, 8);
    }
    if (mip) gl.generateMipmap(gl.TEXTURE_2D);
    return t;
  }

  /**
   * Scene textures ship as opaque .bin files (see pb-mockup-flow/live/mask_assets.py):
   * Shopify's CDN sniffs PNG/JPEG content and swaps in lossy WebP/AVIF, which garbles
   * the UV data. Layout: "PBLV", kind ("P" png | "J" jpeg), 3 pad bytes, the file's
   * first 8 bytes XOR 0xA5, then the rest of the file.
   */
  async function loadAsset(sid, name) {
    const res = await fetch(assetUrl(sid, name), { credentials: "omit", priority: "low" });
    if (!res.ok) throw new Error(`PBLive: ${name} ${res.status}`);
    const buf = new Uint8Array(await res.arrayBuffer());
    const tag = String.fromCharCode(buf[0], buf[1], buf[2], buf[3]);
    if (tag !== "PBLV" || buf.length < 17) throw new Error(`PBLive: ${sid}-${name} is not a PBLV asset`);
    const type = buf[4] === 80 /* P */ ? "image/png" : "image/jpeg";
    const file = buf.subarray(8);
    for (let k = 0; k < 8; k++) file[k] ^= 0xa5;
    const blob = new Blob([file], { type });
    return createImageBitmap(blob, { colorSpaceConversion: "none", premultiplyAlpha: "none" });
  }

  /** Scene textures load after first paint + idle, one scene at a time, needed scenes first. */
  function startLoading() {
    if (loading) return;
    loading = true;
    const go = async () => {
      try {
        // UV data first, for every scene: nothing renders unless all of it is intact.
        const uv = {};
        for (const sid of ORDER) {
          const imgs = await Promise.all(FILES.slice(3).map((f) => loadAsset(sid, f)));
          if (dead) return;
          const texs = imgs.map((img) => texture(img, { nearest: true }));
          if (!uvIntact(texs[0], imgs[0], UV_SUM[sid][0]) || !uvIntact(texs[1], imgs[1], UV_SUM[sid][1])) {
            console.warn(`[PBLive] ${sid} UV data arrived altered; live mockups disabled`);
            disable();
            return;
          }
          uv[sid] = texs;
        }
        const wanted = new Set([...targets.values()].map((t) => t.sid));
        for (const sid of [...ORDER.filter((s) => wanted.has(s)), ...ORDER.filter((s) => !wanted.has(s))]) {
          const imgs = await Promise.all(FILES.slice(0, 3).map((f) => loadAsset(sid, f)));
          if (dead) return;
          // base/light/curve get mipmaps so small thumbnails don't alias.
          scenes[sid] = [...imgs.map((img) => texture(img, { mip: true })), ...uv[sid]];
          update();
        }
      } catch (err) {
        console.warn("[PBLive]", err);
        disable();
      }
    };
    const idle = () => (window.requestIdleCallback ? requestIdleCallback(go, { timeout: 3000 }) : setTimeout(go, 200));
    if (document.readyState === "complete") idle();
    else window.addEventListener("load", idle, { once: true });
  }

  /** Reads a data texture back from the GPU and checks it against the baked checksum. */
  function uvIntact(tex, img, expected) {
    const w = img.width, h = img.height;
    const fb = gl.createFramebuffer();
    gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
    const px = new Uint8Array(w * h * 4);
    gl.readPixels(0, 0, w, h, gl.RGBA, gl.UNSIGNED_BYTE, px);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.deleteFramebuffer(fb);
    let sum = 0;
    for (let i = 0; i < px.length; i += 4) sum = (sum + px[i] + px[i + 1] + px[i + 2]) >>> 0;
    return sum === expected;
  }

  /** Permanently off for this page: targets never get .pb-live-on, static images stay. */
  function disable() {
    dead = true;
    for (const [el, t] of targets) if (t.ready) { el.classList.remove("pb-live-on"); t.ready = false; }
    targets.clear();
  }

  function sizeFor(el) {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const css = el.clientWidth || el.width || 300;
    return Math.max(16, Math.min(MAX_PX, Math.round(css * dpr)));
  }

  function uploadPattern() {
    if (!source || !source.width || !source.height) return false;
    let src = source;
    if (Math.max(src.width, src.height) > MAX_PATTERN) {
      const k = MAX_PATTERN / Math.max(src.width, src.height);
      const c = document.createElement("canvas");
      c.width = Math.round(src.width * k);
      c.height = Math.round(src.height * k);
      c.getContext("2d").drawImage(src, 0, 0, c.width, c.height);
      src = c;
    }
    gl.activeTexture(gl.TEXTURE5);
    patTex = texture(src, { repeat: true, mip: true }, patTex);
    patAspect = src.height / src.width;
    return true;
  }

  function frame() {
    raf = 0;
    if (dead || !gl) return;
    const todo = [...targets].filter(([el, t]) => scenes[t.sid] && el.isConnected && visible.get(el) !== false);
    if (!todo.length) return;
    if (patternDirty || !patTex) {
      if (!uploadPattern()) return;
      patternDirty = false;
    }
    const need = Math.max(...todo.map(([el]) => sizeFor(el)));
    if (glCanvas.width < need || glCanvas.height < need) { glCanvas.width = glCanvas.height = need; }
    gl.useProgram(prog);
    gl.activeTexture(gl.TEXTURE5);
    gl.bindTexture(gl.TEXTURE_2D, patTex);
    gl.uniform2f(uni.uRepeat, repeatIn, repeatIn * patAspect);
    for (const [el, t] of todo) {
      const size = sizeFor(el);
      if (el.width !== size || el.height !== size) { el.width = size; el.height = size; }
      const sc = SCENES[t.sid];
      scenes[t.sid].forEach((tex, k) => { gl.activeTexture(gl.TEXTURE0 + k); gl.bindTexture(gl.TEXTURE_2D, tex); });
      const T = new Float32Array(16);
      sc.t.forEach((v, k) => T.set([v[0], v[1], v[2], v[3] || 0], k * 4));
      gl.uniform4fv(uni.uT, T);
      gl.uniform1f(uni.uN, sc.n);
      gl.uniform1f(uni.uRot, sc.r || 0);
      gl.viewport(0, 0, size, size);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      // WebGL's origin is bottom-left: the frame sits in the canvas's bottom-left corner.
      const ctx = el.getContext("2d");
      ctx.drawImage(glCanvas, 0, glCanvas.height - size, size, size, 0, 0, size, size);
      if (!t.ready) {
        t.ready = true;
        el.classList.add("pb-live-on");
        el.dispatchEvent(new CustomEvent("pblive:ready", { bubbles: true, detail: { scene: t.sid } }));
      }
    }
  }

  function update() {
    patternDirty = true;
    if (!raf && !dead) raf = requestAnimationFrame(frame);
  }

  function add(el, sid) {
    if (!supported() || !(el instanceof HTMLCanvasElement)) return false;
    sid = sid || el.dataset.pbScene;
    if (!SCENES[sid]) return false;
    targets.set(el, { sid, ready: false });
    if (window.IntersectionObserver) {
      io = io || new IntersectionObserver((entries) => {
        for (const e of entries) visible.set(e.target, e.isIntersecting);
        update();
      }, { rootMargin: "200px" });
      io.observe(el);
    }
    startLoading();
    update();
    return true;
  }

  function remove(el) {
    targets.delete(el);
    if (io) io.unobserve(el);
  }

  function mount(els, sourceCanvas, opts = {}) {
    if (!supported()) return false;
    source = sourceCanvas;
    if (opts.repeatIn) repeatIn = opts.repeatIn;
    Array.from(els || []).forEach((el, i) => add(el, el.dataset.pbScene || ORDER[i]));
    return true;
  }

  function setSource(sourceCanvas) {
    source = sourceCanvas;
    update();
  }

  window.PBLive = { mount, update, add, remove, setSource, supported, scenes: ORDER.slice() };
})();
